import { useQueryClient } from "@tanstack/react-query"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "design-system/atoms/Tooltip"
import { SECONDS_TO_MILLIS } from "lopermut"
import { CalendarClock, CalendarX, Pencil, Timer } from "lucide-react"
import { useCancelCampaignLaunchMutation } from "modules/campaigns/hooks/useCancelCampaignLaunchMutation"
import { useLaunchAudienceLabel } from "modules/campaigns/hooks/useLaunchAudienceLabel"
import { useUpdateCampaignLaunchMutation } from "modules/campaigns/hooks/useUpdateCampaignLaunchMutation"
import { getCampaignQueryKey } from "modules/campaigns/lib/campaignQuery"
import { formatLaunchTime, isImminent, isUpcoming } from "modules/campaigns/lib/launchSchedule"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { CampaignDetailResponseDataSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { LaunchScheduleInput } from "./ManualLaunch/LaunchScheduleInput"

type Campaign = z.infer<typeof CampaignDetailResponseDataSchema>

type Props = {
  campaign: Campaign
}

export function ScheduledLaunches({ campaign }: Props) {
  const scheduledLaunches = campaign.scheduledLaunches ?? []

  if (scheduledLaunches.length === 0) return null

  return (
    <div className="flex flex-col divide-y rounded-lg border bg-background">
      {scheduledLaunches.map((launch) => (
        <ScheduledLaunch key={launch.id} campaignId={campaign.id} launch={launch} />
      ))}
    </div>
  )
}

type ScheduledLaunchProps = {
  campaignId: string
  launch: NonNullable<Campaign["scheduledLaunches"]>[number]
}

function ScheduledLaunch({ campaignId, launch }: ScheduledLaunchProps) {
  const { t, i18n } = useTranslation()
  const { describeAudience } = useLaunchAudienceLabel()
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [newScheduledAt, setNewScheduledAt] = useState<Date>()

  const { mutateAsync: updateCampaignLaunch, isPending: isRescheduling } =
    useUpdateCampaignLaunchMutation(campaignId)
  const { mutateAsync: cancelCampaignLaunch, isPending: isCancelling } =
    useCancelCampaignLaunchMutation(campaignId)

  const secondsLeft = useSecondsUntilImminentLaunch(campaignId, new Date(launch.scheduledAt))

  const isBusy = isRescheduling || isCancelling
  const canReschedule = Boolean(newScheduledAt && isUpcoming(newScheduledAt))
  const scheduledAtLabel = formatLaunchTime(new Date(launch.scheduledAt), i18n.language)

  function startRescheduling() {
    setNewScheduledAt(new Date(launch.scheduledAt))
    setIsRescheduleDialogOpen(true)
  }

  async function handleReschedule() {
    if (!newScheduledAt || !isUpcoming(newScheduledAt)) return

    await updateCampaignLaunch({ launchId: launch.id, scheduledAt: newScheduledAt.toISOString() })

    toast.success(
      t("campaigns.scheduledLaunch.rescheduleSuccess", {
        date: formatLaunchTime(newScheduledAt, i18n.language),
      }),
    )
    setIsRescheduleDialogOpen(false)
  }

  function renderTitle() {
    if (secondsLeft === undefined) {
      return t("campaigns.scheduledLaunch.title", { date: scheduledAtLabel })
    }

    if (secondsLeft === 0) return t("campaigns.scheduledLaunch.starting")

    return t("campaigns.scheduledLaunch.startsIn", { time: formatCountdown(secondsLeft) })
  }

  async function handleCancel() {
    await cancelCampaignLaunch(launch.id)

    toast.success(t("campaigns.scheduledLaunch.cancelSuccess"))
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 p-4">
      <div className="flex items-center gap-3">
        {secondsLeft === undefined ? (
          <CalendarClock className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <Timer className="size-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{renderTitle()}</span>
          <span className="text-sm text-muted-foreground">
            {t("campaigns.scheduledLaunch.audience", { count: launch.audience })}
          </span>
          <span className="text-sm text-muted-foreground break-words">
            {describeAudience(launch.audienceSource)}
          </span>
          {launch.closeOngoingConversations && (
            <span className="text-sm text-muted-foreground">
              {t("campaigns.scheduledLaunch.closesOngoingConversations")}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label={t("campaigns.scheduledLaunch.reschedule")}
                onClick={startRescheduling}
              >
                <Pencil className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("campaigns.scheduledLaunch.reschedule")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button variant="destructive" onClick={() => setIsCancelDialogOpen(true)}>
          <CalendarX className="size-4" />
          {t("campaigns.scheduledLaunch.cancel")}
        </Button>
      </div>
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("campaigns.scheduledLaunch.rescheduleTitle")}</DialogTitle>
            <DialogDescription>
              {t("campaigns.scheduledLaunch.rescheduleDescription", { date: scheduledAtLabel })}
            </DialogDescription>
          </DialogHeader>
          <LaunchScheduleInput
            id={`reschedule-launch-${launch.id}`}
            defaultValue={newScheduledAt}
            disabled={isBusy}
            onChange={setNewScheduledAt}
          />
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsRescheduleDialogOpen(false)}
              disabled={isBusy}
            >
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleReschedule} disabled={isBusy || !canReschedule}>
              {t("campaigns.scheduledLaunch.saveReschedule")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("campaigns.scheduledLaunch.cancelTitle")}</DialogTitle>
            <DialogDescription>
              {t("campaigns.scheduledLaunch.cancelDescription", {
                count: launch.audience,
                date: scheduledAtLabel,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsCancelDialogOpen(false)}
              disabled={isBusy}
            >
              {t("campaigns.scheduledLaunch.keep")}
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={isBusy}>
              {t("campaigns.scheduledLaunch.confirmCancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function useSecondsUntilImminentLaunch(campaignId: string, scheduledAt: Date) {
  const queryClient = useQueryClient()
  const [now, setNow] = useState(Date.now)

  const isImminentLaunch = isImminent(scheduledAt)
  const secondsLeft = Math.max(0, Math.ceil((scheduledAt.getTime() - now) / SECONDS_TO_MILLIS))
  const hasStarted = isImminentLaunch && secondsLeft === 0

  useEffect(() => {
    if (!isImminentLaunch || hasStarted) return

    const interval = setInterval(() => setNow(Date.now()), SECONDS_TO_MILLIS)
    return () => clearInterval(interval)
  }, [isImminentLaunch, hasStarted])

  useEffect(() => {
    if (!hasStarted) return

    const timeout = setTimeout(
      () => queryClient.invalidateQueries({ queryKey: getCampaignQueryKey(campaignId) }),
      5 * SECONDS_TO_MILLIS,
    )
    return () => clearTimeout(timeout)
  }, [hasStarted, queryClient, campaignId])

  return isImminentLaunch ? secondsLeft : undefined
}

function formatCountdown(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = String(totalSeconds % 60).padStart(2, "0")

  return `${minutes}:${seconds}`
}
